import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, TrendingUp, Calculator, PieChart, Home, Palmtree } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-warm-sand">

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1693652011763-bf39229a36d3?q=80&w=2070&auto=format&fit=crop)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#005F73]/95 to-[#0A9396]/90"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-32">
          <div className="flex items-center gap-2 mb-8">
            <Palmtree className="w-8 h-8 text-[#94D2BD]" strokeWidth={1.5} />
            <span className="text-white/90 text-lg font-medium tracking-wide">GwadImmo Invest</span>
          </div>
          
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-semibold text-white leading-tight tracking-tight mb-6 max-w-4xl">
            Investir en Outre-mer avec précision
          </h1>
          
          <p className="text-base sm:text-xl md:text-2xl text-white/90 leading-relaxed mb-12 max-w-2xl">
            Simulez la rentabilité de vos projets immobiliers dans les DROM : Guadeloupe, Martinique, Guyane, La Réunion, Mayotte. Offre Découverte à seulement 2 €.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <Button 
              data-testid="start-calculation-btn"
              onClick={() => isAuthenticated ? navigate('/calculator') : navigate('/register')}
              className="rounded-full px-8 py-6 text-lg font-medium bg-[#EE9B00] hover:bg-[#EE9B00]/90 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
            >
              <Calculator className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {isAuthenticated ? 'Calculer ma rentabilité' : 'Découvrir pour 2 €'}
            </Button>
            
            <Button 
              data-testid="view-pricing-btn"
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="rounded-full px-8 py-6 text-lg font-medium border-2 border-white/30 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all"
            >
              Voir les tarifs
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-semibold text-[#005F73] mb-4 tracking-tight">
            Tout pour réussir votre investissement
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Des calculs précis, des outils professionnels, adaptés aux marchés ultramarins
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            data-testid="feature-card-calculations"
            className="bg-white rounded-2xl border border-stone-100 p-8 hover:shadow-[0_8px_30px_-4px_rgba(0,95,115,0.2)] transition-all duration-300 group"
          >
            <div className="w-14 h-14 rounded-xl bg-[#005F73]/10 flex items-center justify-center mb-6 group-hover:bg-[#005F73]/20 transition-all">
              <TrendingUp className="w-7 h-7 text-[#005F73]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-semibold text-[#005F73] mb-3">Calculs complets</h3>
            <p className="text-stone-600 leading-relaxed">
              Rentabilité brute et nette, cash-flow mensuel, ROI détaillé avec prise en compte de tous vos paramètres.
            </p>
          </div>

          <div 
            data-testid="feature-card-tax"
            className="bg-white rounded-2xl border border-stone-100 p-8 hover:shadow-[0_8px_30px_-4px_rgba(0,95,115,0.2)] transition-all duration-300 group"
          >
            <div className="w-14 h-14 rounded-xl bg-[#EE9B00]/10 flex items-center justify-center mb-6 group-hover:bg-[#EE9B00]/20 transition-all">
              <Building2 className="w-7 h-7 text-[#EE9B00]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-semibold text-[#005F73] mb-3">Défiscalisation</h3>
            <p className="text-stone-600 leading-relaxed">
              Intégration des dispositifs Pinel Outre-Mer et Girardin pour optimiser votre fiscalité.
            </p>
          </div>

          <div 
            data-testid="feature-card-projects"
            className="bg-white rounded-2xl border border-stone-100 p-8 hover:shadow-[0_8px_30px_-4px_rgba(0,95,115,0.2)] transition-all duration-300 group"
          >
            <div className="w-14 h-14 rounded-xl bg-[#94D2BD]/20 flex items-center justify-center mb-6 group-hover:bg-[#94D2BD]/30 transition-all">
              <Home className="w-7 h-7 text-[#0A9396]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-semibold text-[#005F73] mb-3">Multi-projets</h3>
            <p className="text-stone-600 leading-relaxed">
              Sauvegardez et comparez plusieurs projets pour prendre les meilleures décisions d'investissements.
            </p>
          </div>

          <div 
            data-testid="feature-card-visualization"
            className="bg-white rounded-2xl border border-stone-100 p-8 hover:shadow-[0_8px_30px_-4px_rgba(0,95,115,0.2)] transition-all duration-300 group"
          >
            <div className="w-14 h-14 rounded-xl bg-[#0A9396]/10 flex items-center justify-center mb-6 group-hover:bg-[#0A9396]/20 transition-all">
              <PieChart className="w-7 h-7 text-[#0A9396]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-semibold text-[#005F73] mb-3">Visualisations</h3>
            <p className="text-stone-600 leading-relaxed">
              Graphiques et tableaux pour comprendre en un coup d'œil la performance de vos investissements.
            </p>
          </div>

          <div 
            data-testid="feature-card-instant"
            className="bg-white rounded-2xl border border-stone-100 p-8 hover:shadow-[0_8px_30px_-4px_rgba(0,95,115,0.2)] transition-all duration-300 group"
          >
            <div className="w-14 h-14 rounded-xl bg-[#EE9B00]/10 flex items-center justify-center mb-6 group-hover:bg-[#EE9B00]/20 transition-all">
              <Calculator className="w-7 h-7 text-[#EE9B00]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-semibold text-[#005F73] mb-3">Résultats instantanés</h3>
            <p className="text-stone-600 leading-relaxed">
              Calculs en temps réel pour ajuster vos paramètres et optimiser votre stratégie d'investissement.
            </p>
          </div>

          <div 
            data-testid="feature-card-guadeloupe"
            className="bg-white rounded-2xl border border-stone-100 p-8 hover:shadow-[0_8px_30px_-4px_rgba(0,95,115,0.2)] transition-all duration-300 group"
          >
            <div className="w-14 h-14 rounded-xl bg-[#94D2BD]/20 flex items-center justify-center mb-6 group-hover:bg-[#94D2BD]/30 transition-all">
              <Palmtree className="w-7 h-7 text-[#0A9396]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-semibold text-[#005F73] mb-3">Spécial Outre-mer</h3>
            <p className="text-stone-600 leading-relaxed">
              Paramètres adaptés aux spécificités des marchés immobiliers des DROM et leur fiscalité unique (Pinel OM, Girardin).
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[#005F73] to-[#0A9396] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6 tracking-tight">
            Prêt à calculer votre rentabilité ?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Créez votre compte et lancez votre première simulation dès 2 €.
          </p>
          <Button 
            data-testid="cta-start-btn"
            onClick={() => navigate('/calculator')}
            className="rounded-full px-10 py-7 text-xl font-medium bg-[#EE9B00] hover:bg-[#EE9B00]/90 text-white shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
          >
            Commencer ma simulation
          </Button>
        </div>
      </section>
    </div>
  );
}
